// Order Fulfillment Visibility (sc-5858) — internal CSR view of a location's
// orders vs on-hand per order, from the ledger. Rendered in the Orders tab
// (FulfillmentOrderCard) and the inventory SKU-row expansion (SkuOrderDetail).
import React from 'react';
import { CircularProgress } from '@mui/material';
import type {
  FulfillmentBeacon, FulfillmentSku, FulfillmentOrder, LocationOrderFulfillmentResponse,
} from '../../api/config';
import { hexahqOrderUrl, hexahqQuoteUrl } from '../../utils/hexahq';

export const fmtDateTime = (s?: string | null) => (s ? new Date(s).toLocaleString() : '—');
const fmtDateShort = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');

// "Order #N" linking out to the HexaHQ order in a new tab. stopPropagation so a
// click doesn't also toggle the surrounding expandable row/card.
export const OrderLink: React.FC<{ id: number }> = ({ id }) => (
  <a href={hexahqOrderUrl(id)} target="_blank" rel="noopener noreferrer"
     onClick={e => e.stopPropagation()} title={`Open order ${id} in HexaHQ`}
     style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
    Order #{id}
    <span className="material-icons-outlined" style={{ fontSize: 12, verticalAlign: 'middle', marginLeft: 2 }}>open_in_new</span>
  </a>
);

// "Quote #N" linking out to the HexaHQ quote in a new tab — sibling of OrderLink.
export const QuoteLink: React.FC<{ id: number }> = ({ id }) => (
  <a href={hexahqQuoteUrl(id)} target="_blank" rel="noopener noreferrer"
     onClick={e => e.stopPropagation()} title={`Open quote ${id} in HexaHQ`}
     style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
    Quote #{id}
    <span className="material-icons-outlined" style={{ fontSize: 12, verticalAlign: 'middle', marginLeft: 2 }}>open_in_new</span>
  </a>
);

// Roster badge for a single tagged beacon's current ledger state.
export const beaconBadge = (b: FulfillmentBeacon): { label: string; cls: string } => {
  // soft-present: DEPARTING but the device went quiet — still here, counts as on hand
  if (b.soft_present) return { label: 'on hand · gateway quiet', cls: 'chip-success' };
  if (b.state === 'PRESENT' && b.on_site) return { label: 'on hand', cls: 'chip-success' };
  if (b.state === 'PRESENT' && !b.on_site) {
    const where = b.present_location?.name ?? (b.present_location ? `loc ${b.present_location.id}` : 'elsewhere');
    return { label: `at ${where}`, cls: 'chip-info' };
  }
  if (b.state === 'DEPARTING') return { label: 'departing', cls: 'chip-warning' }; // real shipout — not counted
  if (b.state === 'ABSENT') return { label: 'absent', cls: 'chip-default' };
  return { label: 'unknown', cls: 'chip-default' };
};

const BeaconRoster: React.FC<{ beacons: FulfillmentBeacon[] }> = ({ beacons }) => {
  if (beacons.length === 0) return <div className="text-muted" style={{ padding: '6px 10px', fontSize: 11.5 }}>No tags.</div>;
  return (
    <table className="data-table data-table-nested">
      <thead>
        <tr><th>Beacon</th><th>Arrived on site</th><th>Last seen</th><th>State</th></tr>
      </thead>
      <tbody>
        {beacons.map(b => {
          const badge = beaconBadge(b);
          return (
            <tr key={b.beacon_id}>
              <td className="text-mono" style={{ fontSize: 11.5 }}>
                {b.beacon_id}
                {b.sku_mismatch && (
                  <span className="chip chip-warning" style={{ marginLeft: 6, fontSize: 9 }}
                        title="Tagged SKU differs from the ledger SKU at this location">SKU mismatch</span>
                )}
              </td>
              <td style={{ fontSize: 11.5 }}>{fmtDateTime(b.arrived_at)}</td>
              <td style={{ fontSize: 11.5 }}>{fmtDateTime(b.last_seen_at)}</td>
              <td><span className={`chip ${badge.cls}`} style={{ fontSize: 10 }}>{badge.label}</span></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// One SKU within an order: ordered · tagged · on-hand, expandable to its roster.
const FulfillmentSkuRow: React.FC<{ row: FulfillmentSku }> = ({ row }) => {
  const [open, setOpen] = React.useState(false);
  const hasRoster = row.beacons.length > 0;
  const missing = row.tagged - row.on_hand;
  return (
    <>
      <div className={`pe-sku-row ${row.exception ? 'sku-oos' : ''}`}
           onClick={() => hasRoster && setOpen(o => !o)}
           style={{ cursor: hasRoster ? 'pointer' : 'default' }}>
        <span className="pe-sku-name">{row.sku}</span>
        {row.tracked ? (
          <span style={{ fontSize: 12, marginLeft: 14 }}>
            ordered <strong>{row.ordered}</strong> · tagged <strong>{row.tagged}</strong> · on hand <strong>{row.on_hand}</strong>
          </span>
        ) : (
          <span style={{ fontSize: 12, marginLeft: 14 }}>ordered <strong>{row.ordered}</strong> · <span className="text-muted">not tracked</span></span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {row.soft_present && (
            <span className="chip chip-default" style={{ fontSize: 9 }}
                  title="On-hand count includes soft-present tags: DEPARTING but the gateway went quiet, so the tag is almost certainly still here">incl. soft-present</span>
          )}
          {row.exception && (
            <span className="chip chip-danger" style={{ fontSize: 9 }}
                  title="Tagged to this order but not on hand">{missing} not on hand</span>
          )}
          {hasRoster && (
            <span className={`material-icons pe-card-expand ${open ? 'open' : ''}`} style={{ fontSize: 18 }}>chevron_right</span>
          )}
        </span>
      </div>
      {open && hasRoster && (
        <div style={{ padding: '4px 10px 10px', overflowX: 'auto' }}><BeaconRoster beacons={row.beacons} /></div>
      )}
    </>
  );
};

// One order: exception-led header (flagged when any line has on_hand < tagged),
// expandable to per-SKU rollup.
export const FulfillmentOrderCard: React.FC<{ order: FulfillmentOrder }> = ({ order }) => {
  const [open, setOpen] = React.useState(false);
  const notPresent = order.skus.reduce((n, s) => n + Math.max(0, s.tagged - s.on_hand), 0);
  const softPresent = order.skus.some(s => s.soft_present);
  return (
    <div className={`pe-card ${notPresent > 0 ? 'pe-oos' : ''}`}>
      <div className="pe-card-header" onClick={() => setOpen(o => !o)}>
        <div className="pe-card-title-row">
          <span className="pe-card-title"><OrderLink id={order.hexahq_order_id} /></span>
          {order.po_number && <span className="pe-card-category text-mono">{order.po_number}</span>}
          {order.status && <span className="chip chip-default" style={{ fontSize: 10 }}>{order.status}</span>}
          {notPresent > 0 && (
            <span className="chip chip-danger" style={{ fontSize: 10 }}
                  title="Tagged to this order but not on hand at this location">{notPresent} of {order.skus.reduce((n, s) => n + s.tagged, 0)} tags not on hand</span>
          )}
          {softPresent && (
            <span className="chip chip-default" style={{ fontSize: 10 }}
                  title="On-hand count includes soft-present tags (gateway quiet)">incl. soft-present</span>
          )}
          <span className={`material-icons pe-card-expand ${open ? 'open' : ''}`}>chevron_right</span>
        </div>
        <div className="pe-card-stats">
          <span className="pe-card-count text-muted" style={{ fontSize: 11.5 }}>
            {order.skus.length} {order.skus.length === 1 ? 'SKU' : 'SKUs'} · ordered {fmtDateShort(order.ordered_at)}
          </span>
        </div>
      </div>
      <div className={`pe-card-body ${open ? '' : 'collapsed'}`}>
        {order.skus.length === 0
          ? <div className="text-muted" style={{ padding: '6px 10px', fontSize: 11.5 }}>No line items.</div>
          : order.skus.map(s => <FulfillmentSkuRow key={s.sku} row={s} />)}
      </div>
    </div>
  );
};

// Inline per-SKU order detail (shown when an inventory SKU row is expanded): the
// SKU's on-hand ledger tags grouped by order — each order links out to HexaHQ —
// plus a "no order" residual. Real tags, so it can't contradict the row count.
export const SkuOrderDetail: React.FC<{
  sku: string;
  data?: LocationOrderFulfillmentResponse;
  loading: boolean;
}> = ({ sku, data, loading }) => {
  const groups = React.useMemo(() => {
    if (!data) return [];
    const rows = data.orders
      .map(o => ({ order: o, row: o.skus.find(s => s.sku === sku) }))
      .filter(({ row }) => row && (row.on_hand > 0 || row.tagged > 0))
      .map(({ order, row }) => ({
        key: `order-${order.hexahq_order_id}`,
        orderId: order.hexahq_order_id as number | null,
        on_hand: row!.on_hand,
        beacons: row!.beacons.filter(b => b.on_hand), // on-hand tags (PRESENT or soft-present)
      }));
    const residual = [...data.residual.unattributed, ...data.residual.out_of_window]
      .filter(r => r.sku === sku && r.on_hand > 0);
    if (residual.length) {
      rows.push({
        key: 'no-order',
        orderId: null,
        on_hand: residual.reduce((n, r) => n + r.on_hand, 0),
        beacons: residual.flatMap(r => r.beacons),
      });
    }
    return rows;
  }, [sku, data]);

  return (
    <div style={{ padding: '2px 14px 10px 24px' }}>
      {loading && <div className="text-muted" style={{ fontSize: 11.5 }}><CircularProgress size={14} /> Loading orders…</div>}
      {!loading && data?.degraded && (
        <div className="text-muted" style={{ fontSize: 11.5, color: 'var(--warning-text, #8a6d00)' }}>
          Order attribution temporarily unavailable (HQ replica) — showing on-hand only.
        </div>
      )}
      {!loading && !data?.degraded && groups.length === 0 && (
        <div className="text-muted" style={{ fontSize: 11.5 }}>No on-hand tags for this SKU.</div>
      )}
      {!loading && groups.map(g => (
        <div key={g.key} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 2 }}>
            {g.orderId ? <OrderLink id={g.orderId} /> : <span className="text-muted">No order (residual)</span>}
            <span className="text-muted"><strong>{g.on_hand}</strong> on hand</span>
          </div>
          <div style={{ overflowX: 'auto' }}><BeaconRoster beacons={g.beacons} /></div>
        </div>
      ))}
    </div>
  );
};
